const Shimmer = ({ className = "", style }) => (
  <span
    className={`shimmer-block ${className}`}
    style={{ display: "block", borderRadius: 6, ...style }}
  />
);

export const FullPageShimmer = () => (
  <div className="min-h-screen bg-white">
    {/* Header: hamburger · round logo · name/subtitle · lang + cart */}
    <div className="bg-white px-4">
      <div className="flex flex-row pt-4 pb-3 items-center">
        <Shimmer style={{ width: 24, height: 24, borderRadius: 6 }} />
        <Shimmer
          className="ml-2 mr-2"
          style={{ width: 40, height: 40, borderRadius: 999, flexShrink: 0 }}
        />
        <div className="flex flex-col gap-2 flex-1 min-w-0 ml-1 mr-2">
          <Shimmer style={{ width: "55%", height: 18 }} />
          <Shimmer style={{ width: "35%", height: 11 }} />
        </div>
        <div className="ml-auto flex flex-row space-x-3 flex-shrink-0">
          <Shimmer style={{ width: 32, height: 32, borderRadius: 8 }} />
          <Shimmer style={{ width: 38, height: 38, borderRadius: 10 }} />
        </div>
      </div>
    </div>

    {/* Trust strip — full-width dark bar, 3 cells */}
    <div className="flex bg-ink-900">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex-1 flex justify-center py-[9px] px-[5px]"
        >
          <Shimmer style={{ width: "70%", height: 18, opacity: 0.35 }} />
        </div>
      ))}
    </div>

    {/* Shipping announcement — slim lavender bar */}
    <div className="flex justify-center bg-[#F4F5FF] border-b border-s-primary/[0.12] px-4 py-[10px]">
      <Shimmer style={{ width: "72%", height: 12 }} />
    </div>

    {/* Owner band — white pill */}
    <div
      className="flex items-center gap-[10px] mx-4 my-[12px] px-[12px] py-[8px] bg-white border border-wc-border rounded-pill"
      style={{ boxShadow: "0 4px 18px rgba(31,33,46,.08)" }}
    >
      <Shimmer style={{ width: 34, height: 34, borderRadius: 999, flexShrink: 0 }} />
      <div className="flex flex-col gap-[6px]">
        <Shimmer style={{ width: 120, height: 12 }} />
        <Shimmer style={{ width: 160, height: 9 }} />
      </div>
      <Shimmer className="ml-auto" style={{ width: 74, height: 11, flexShrink: 0 }} />
    </div>

    {/* Search bar */}
    <div className="flex items-center gap-[10px] mx-4 mb-4 bg-white border border-wc-border rounded-lg px-[15px] py-[13px]">
      <Shimmer style={{ width: 19, height: 19, borderRadius: 999, flexShrink: 0 }} />
      <Shimmer style={{ width: "62%", height: 13 }} />
    </div>

    {/* Newly Arrived — heading + a scrolling row. Mirrors NewlyArrivedRow's rail, which is now the
        primary shape: the 2x2 grid was dropped on 2026-09-03 and everything from 3 items up
        scrolls. Cell widths are the rail's own, so a tile is 184.5px at 412px and 253.8px at
        1440px and desktop does not reflow on load. Overflow is clipped rather than scrollable —
        a skeleton should not invite a swipe before the real cards exist.
        Only a 2-product response now settles into a different shape (a plain row of two), and the
        shimmer cannot know the count before the payload lands. */}
    <section className="pt-[6px] pb-[10px]">
      <div className="px-4 pb-[11px]">
        <Shimmer style={{ width: 128, height: 17, borderRadius: 4 }} />
        <Shimmer style={{ width: "52%", height: 11, borderRadius: 4, marginTop: 5 }} />
      </div>
      <div className="flex gap-[11px] overflow-hidden px-4 pt-[2px] pb-[8px]">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex-none w-[calc((100%_-_11px)/2)] md:w-[calc((100%_-_33px)/4)]"
          >
            <Shimmer style={{ width: "100%", aspectRatio: "3 / 4", borderRadius: 12 }} />
            <Shimmer style={{ width: "92%", height: 12, borderRadius: 4, marginTop: 7 }} />
            <Shimmer style={{ width: 52, height: 13, borderRadius: 4, marginTop: 6 }} />
          </div>
        ))}
      </div>
      {/* The progress-bar track, so the row's height matches the real rail's and nothing jumps —
          including its mt-[6px], or the bar would shift 6px when the payload lands. */}
      <div className="mx-4 mt-[6px] h-[4px] rounded-full bg-wc-border" />
    </section>

    {/* Pinboard shelf — padded card: icon + title, then 4-col tile grid */}
    <section className="mb-[18px]">
      <div className="px-4 pt-[4px] pb-[8px]">
        <div className="rounded-[18px] p-[12px] pb-[10px] border border-[rgba(31,33,46,0.04)] bg-[#F7F8FA] shadow-[0_2px_12px_rgba(31,33,46,0.07)]">
          <div className="flex items-center gap-[8px] pb-[10px]">
            <Shimmer style={{ width: 22, height: 22, borderRadius: 6 }} />
            <Shimmer style={{ width: "45%", height: 14 }} />
          </div>
          <div className="grid grid-cols-4 gap-[8px]">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-[5px]">
                <Shimmer style={{ width: "100%", aspectRatio: "1", borderRadius: 12 }} />
                <Shimmer style={{ width: "80%", height: 9, margin: "0 auto" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-center gap-[6px]">
        <Shimmer style={{ width: 18, height: 6, borderRadius: 999 }} />
        <Shimmer style={{ width: 6, height: 6, borderRadius: 999 }} />
      </div>
    </section>

    {/* Category grids — section title + 4-col subcategory tiles */}
    {[0, 1].map((g) => (
      <div key={g} className="px-4 pt-[6px] pb-[22px]">
        <Shimmer style={{ width: 150, height: 15, marginBottom: 12 }} />
        <div className="grid grid-cols-4 gap-x-[8px] gap-y-[13px]">
          {Array.from({ length: 8 }).map((_, j) => (
            <div key={j} className="flex flex-col items-center gap-[6px]">
              <Shimmer style={{ width: "100%", aspectRatio: "1", borderRadius: 14 }} />
              <Shimmer style={{ width: "70%", height: 10 }} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
